import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const buttonGroupVariants = cva("inline-flex", {
  variants: {
    orientation: {
      horizontal: "flex-row",
      vertical: "flex-col",
    },
    gap: {
      none: "gap-0",
      sm: "gap-1",
      default: "gap-2",
      md: "gap-3",
      lg: "gap-4",
    },
  },
  defaultVariants: {
    orientation: "horizontal",
    gap: "none",
  },
});

export interface ButtonGroupProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof buttonGroupVariants> {
  asChild?: boolean;
}

const ButtonGroup = React.forwardRef<HTMLDivElement, ButtonGroupProps>(
  ({ className, orientation, gap, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "div";
    const childrenArray = React.Children.toArray(children);
    const hasGap = gap && gap !== "none";

    return (
      <Comp
        ref={ref}
        role="group"
        className={cn(buttonGroupVariants({ orientation, gap, className }))}
        {...props}
      >
        {React.Children.map(childrenArray, (child, index) => {
          if (!React.isValidElement(child)) return child;

          const isFirst = index === 0;
          const isLast = index === childrenArray.length - 1;
          const isOnly = childrenArray.length === 1;

          // If there's a gap or only one child, don't modify border radius
          if (isOnly || hasGap) return child;

          // Add specific border radius classes based on position (only when gap is "none")
          const additionalClasses =
            orientation === "vertical"
              ? cn(
                  isFirst && "rounded-b-none rounded-t-[4px]",
                  isLast && "rounded-t-none rounded-b-[4px]",
                  !isFirst && !isLast && "rounded-none"
                )
              : cn(
                  isFirst && "rounded-r-none rounded-l-[4px]",
                  isLast && "rounded-l-none rounded-r-[4px]",
                  !isFirst && !isLast && "rounded-none"
                );

          return React.cloneElement(child, {
            ...child.props,
            className: cn(child.props.className, additionalClasses),
          });
        })}
      </Comp>
    );
  }
);
ButtonGroup.displayName = "ButtonGroup";

const ButtonGroupSeparator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("bg-border", className)}
    {...props}
  />
));
ButtonGroupSeparator.displayName = "ButtonGroupSeparator";

const ButtonGroupText = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      "inline-flex h-9 items-center px-3 text-sm text-muted-foreground",
      className
    )}
    {...props}
  />
));
ButtonGroupText.displayName = "ButtonGroupText";

export { ButtonGroup, ButtonGroupSeparator, ButtonGroupText, buttonGroupVariants };
